import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import MobileHeader from '../components/MobileHeader';
import MobileListCard from '../components/MobileListCard';
import EmptyState from '../components/EmptyState';
import moment from 'moment';

const ReceiptsScreen = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [htmlDialogVisible, setHtmlDialogVisible] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [currentReceiptData, setCurrentReceiptData] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 for preview, 1 for source
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get('/api/school/school-fee-receipt/get-my-receipts', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
 
      setItems(response.data);
    } catch (error) {
      console.error('Error loading receipts:', error);
      handleCatch(error);
      setNotification({
        open: true,
        message: 'Failed to load receipts',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const viewReceipt = async (receiptNo) => {
    try {
      console.log('Loading receipt for preview:', receiptNo);
      setLoading(true);

      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `/api/school/school-fee-receipt/get-print-receipt-document?receiptNo=${receiptNo}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const receiptData = response.data;
      console.log('Receipt data loaded successfully:', receiptData);

      await generateHTMLReceipt(receiptData);
    } catch (error) {
      console.error('View receipt error:', error);
      setNotification({
        open: true,
        message: `Failed to load receipt #${receiptNo}`,
        severity: 'error',
      });
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const generateHTMLReceipt = async (receiptData) => {
    try {
      const htmlContent = generateReceiptHTML(receiptData);

      setCurrentReceiptData(receiptData);
      setHtmlContent(htmlContent);
      setHtmlDialogVisible(true);
      setActiveTab(0); // Reset to preview tab
    } catch (error) {
      console.error('HTML generation error:', error);
      setNotification({
        open: true,
        message: 'Failed to generate receipt',
        severity: 'error',
      });
    }
  };

  const generateReceiptHTML = (data) => {
    const feeDetailsTable =
      data.feeDetails && data.feeDetails.length > 0
        ? `
        <div class="fee-details">
            <h3>Fee Details</h3>
            <div class="table-wrapper">
                <table class="fee-table">
                    <thead>
                        <tr>
                            <th>Fee Type</th>
                            <th>Payable</th>
                            <th>Discount</th>
                            <th>Paid</th>
                            <th>Adjust</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.feeDetails
                          .map(
                            (fee) => `
                        <tr>
                            <td>${fee.feeName || 'N/A'}</td>
                            <td>₹${fee.payable || 0}</td>
                            <td>₹${fee.discAmt || 0}</td>
                            <td>₹${fee.paid || 0}</td>
                            <td>₹${fee.adjust || 0}</td>
                            <td>₹${fee.dues || 0}</td>
                        </tr>
                        `
                          )
                          .join('')}
                    </tbody>
                </table>
            </div>
        </div>
        `
        : '';

    const balanceBillsTable =
      data.balanceBills && data.balanceBills.length > 0
        ? `
        <div class="balance-bills">
            <h3>Outstanding Bills</h3>
            <table class="balance-table">
                <thead>
                    <tr>
                        <th>Month</th>
                        <th>Due Amount</th>
                        <th>Due Date</th>
                    </tr>
                </thead>
                <tbody>
                    ${data.balanceBills
                      .map(
                        (bill) => `
                    <tr>
                        <td>${bill.monthName || 'N/A'}</td>
                        <td>₹${bill.duesAmt || 0}</td>
                        <td>${bill.dueDate ? moment(bill.dueDate).format('DD MMM YYYY') : 'N/A'}</td>
                    </tr>
                    `
                      )
                      .join('')}
                </tbody>
            </table>
        </div>
        `
        : '';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Fee Receipt #${data.receiptNo}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Arial', sans-serif; 
            margin: 20px; 
            background-color: #f9f9f9;
            color: #333;
        }
        .receipt-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 3px solid #003D82;
            padding-bottom: 20px;
        }
        .school-name { 
            font-size: 28px; 
            font-weight: bold; 
            color: #003D82; 
            margin-bottom: 5px;
        }
        .school-details { 
            font-size: 14px; 
            color: #666; 
            margin: 3px 0;
        }
        .receipt-info { 
            background: #f8f9fa;
            border: 2px solid #003D82; 
            padding: 20px; 
            border-radius: 8px;
            margin-bottom: 25px;
        }
        .row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
        }
        .label { font-weight: 600; color: #555; }
        .value { color: #333; }
        .info-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
        }
        .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #003D82;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #003D82;
        }
        .fee-details, .balance-bills {
            margin: 25px 0;
        }
        .fee-details h3, .balance-bills h3 {
            color: #003D82;
            margin-bottom: 15px;
            font-size: 18px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
        }
        th, td {
            padding: 12px;
            text-align: left;
            border: 1px solid #ddd;
        }
        th {
            background-color: #003D82;
            color: white;
            font-weight: 600;
        }
        tr:nth-child(even) {
            background-color: #f8f9fa;
        }
        .total-section {
            background: linear-gradient(135deg, #003D82 0%, #0066CC 100%);
            color: white;
            padding: 25px;
            border-radius: 8px;
            margin: 25px 0;
            text-align: center;
        }
        .total {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
        }
        .amount-words {
            font-size: 14px;
            font-style: italic;
            opacity: 0.9;
        }
        .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
        }
        .signature-box {
            width: 45%;
            text-align: center;
        }
        .signature-line {
            border-top: 2px solid #333;
            padding-top: 10px;
            margin-top: 60px;
            font-size: 14px;
            color: #666;
        }
        .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #999;
            padding-top: 20px;
            border-top: 1px solid #ddd;
        }
        @media print {
            body { margin: 0; background: white; }
            .receipt-container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="header">
            <div class="school-name">${data.header1 || 'School Fee Receipt'}</div>
            <div class="school-details">${data.header2 || ''}</div>
            <div class="school-details">${data.header3 || ''}</div>
            <div class="school-details">${data.header4 || ''}</div>
            <div class="school-details">${data.header5 || ''}</div>
        </div>
        
        <div class="receipt-info">
            <div class="row">
                <span class="label">Receipt No:</span>
                <span class="value" style="font-weight: bold; color: #003D82;">#${data.receiptNo}</span>
            </div>
            <div class="row">
                <span class="label">Receipt Date:</span>
                <span class="value">${moment(data.receiptDate).format('DD MMM YYYY')}</span>
            </div>
            <div class="row">
                <span class="label">Session:</span>
                <span class="value">${data.session || 'N/A'}</span>
            </div>
        </div>
        
        <div class="info-section">
            <div class="section-title">Student Information</div>
            <div class="row">
                <span class="label">Name:</span>
                <span class="value">${data.sName || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Father's Name:</span>
                <span class="value">${data.fName || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Class:</span>
                <span class="value">${data.class || 'N/A'} - ${data.section || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Roll No:</span>
                <span class="value">${data.rollNo || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Admission No:</span>
                <span class="value">${data.admissionNo || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Registration No:</span>
                <span class="value">${data.regNo || 'N/A'}</span>
            </div>
        </div>
        
        <div class="info-section">
            <div class="section-title">Payment Information</div>
            <div class="row">
                <span class="label">Month(s):</span>
                <span class="value">${data.month || 'N/A'}</span>
            </div>
            <div class="row">
                <span class="label">Payment Mode:</span>
                <span class="value">${data.mop || 'N/A'}</span>
            </div>
            ${data.mopNo ? `<div class="row"><span class="label">Reference No:</span><span class="value">${data.mopNo}</span></div>` : ''}
            ${data.mopDate ? `<div class="row"><span class="label">Payment Date:</span><span class="value">${moment(data.mopDate).format('DD MMM YYYY')}</span></div>` : ''}
            ${data.bankName ? `<div class="row"><span class="label">Bank:</span><span class="value">${data.bankName}</span></div>` : ''}
            <div class="row">
                <span class="label">Payable Amount:</span>
                <span class="value">₹${data.payable || 0}</span>
            </div>
            <div class="row">
                <span class="label">Current Balance:</span>
                <span class="value">₹${data.balance || 0}</span>
            </div>
        </div>
        
        ${feeDetailsTable}
        
        <div class="total-section">
            <div class="total">Amount Paid: ₹${data.paid || 0}</div>
            <div class="amount-words">${data.paidInWords || 'Amount in words not available'}</div>
            ${data.remarks ? `<div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.3);"><strong>Remarks:</strong> ${data.remarks}</div>` : ''}
        </div>
        
        ${balanceBillsTable}
        
        <div class="signature-section">
            <div class="signature-box">
                <div class="signature-line">Student/Parent Signature</div>
            </div>
            <div class="signature-box">
                <div class="signature-line">Authorized Signature</div>
            </div>
        </div>
        
        <div class="footer">
            This is a computer-generated receipt and does not require a physical signature.
            <br>For any queries, please contact the school office.
        </div>
    </div>
</body>
</html>
`;
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const onRefresh = async () => {
    await loadData();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="My Receipts"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ReceiptIcon />}
            iconColor="#546E7A"
            title="No Receipts Yet"
            subtitle="Your payment receipts will appear here after you make fee payments."
            actionLabel="Refresh"
            onAction={onRefresh}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              mt: 1,
              mx: 1,
              borderRadius: '12px 12px 0 0',
              overflow: 'hidden',
            }}
          >
            {items.map((item, index) => (
              <MobileListCard
                key={index}
                icon={<ReceiptIcon />}
                iconColor="#546E7A"
                iconBg="rgba(84,110,122,0.1)"
                title={`Receipt #${item.receiptNo}`}
                subtitle={`${moment(item.receiptDate).format('DD MMM YYYY')}  •  ₹${item.amount}`}
                description={item.mode ? `Mode: ${item.mode}` : undefined}
                rightIcon={<VisibilityIcon />}
                onClick={() => viewReceipt(item.receiptNo)}
                showDivider={index < items.length - 1}
                showChevron={false}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* HTML Preview Dialog */}
      <Dialog
        open={htmlDialogVisible}
        onClose={() => setHtmlDialogVisible(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">📄 Receipt Preview</Typography>
            {currentReceiptData && (
              <Typography variant="body2" color="text.secondary">
                Receipt #{currentReceiptData.receiptNo} • {currentReceiptData.sName} • ₹
                {currentReceiptData.paid}
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ px: 3 }}>
          <Tab label="🌐 Live Preview" />
          <Tab label="📋 HTML Source" />
        </Tabs>

        <DialogContent dividers sx={{ p: 0 }}>
          {activeTab === 0 ? (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <iframe
                srcDoc={htmlContent}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title="Receipt Preview"
              />
            </Box>
          ) : (
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', height: '100%', overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                🔍 HTML Source Code:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                }}
              >
                {htmlContent}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setHtmlDialogVisible(false)}>Close</Button>
          <Button variant="contained" onClick={handlePrintReceipt} startIcon={<ReceiptIcon />}>
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceiptsScreen;
